import { MglMarker, MglPopup, MglNavigationControl } from '@advisr/v-mapbox';

// TODO: `mapbox-gl-draw` is currently not compatible with IE11 and cannot be transpiled
// so create a custom polygon-draw functionality based on markers
// import MapboxDraw from '@mapbox/mapbox-gl-draw'
// import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'

import MapsMixin from '@/components/core/mapsMixin';
import moment from 'moment';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

// const mapDefaultZoom = 16.5;
// const mapHeaderZoom = 12;

const kGeoGeometryLayerId = 'geo_geometry_layer';
export default {
  name: 'CoreMaps',
  components: {
    MglMarker,
    MglNavigationControl,
    MglPopup
  },
  mixins: [MapsMixin],
  props: {
    geoPolygon: {
      type: Array,
      required: false,
      default: () => []
    },
    markerDraggable: {
      type: Boolean,
      required: false,
      default: () => true
    },
    showPolygonControls: {
      type: Boolean,
      required: false,
      default: () => true
    },
    disablePolygonDrawing: {
      type: Boolean,
      required: false,
      default: () => false
    },
    hideFullScreenButton: {
      type: Boolean,
      required: false,
      default: false
    },
    fullScreen: {
      type: Boolean,
      required: false,
      default: false
    },
    favorite: {
      type: Boolean,
      required: false,
      default: false
    },
    mapInteractive: {
      type: Boolean,
      required: false,
      default: true
    },
    allowScrollZoom: {
      type: Boolean,
      required: false,
      default: false
    },
    geoHistory: {
      type: Array,
      required: false,
      default: null
    }
  },
  data() {
    return {
      historyIndex: -1,
      hasValidPolygon: false,
      // TODO: `mapbox-gl-draw` is currently not compatible with IE11 and cannot be transpiled
      // so create a custom polygon-draw functionality based on markers
      drawControls: null,
      polygonEdit: null,
      // if the "polygon"-draw-mode is entered, first the current polygon is deleted
      // do not publish that following delete-event there, until the polygon-mode is left again
      polygonRedrawMode: false,
      // any animations in the map are overwritten if a new one starts (`flyTo`)
      // as they return a promise, keep this queue going to start one animation after another
      animationQueue: []
    };
  },
  computed: {
    classObject() {
      return `twinMap ${this.fullScreen ? 'fullScreenMap' : 'regularMap'}`;
    },
    markerCoordinates() {
      if ((this.geoHistory || []).length > 1) {
        if (get(this.geoHistory, `[${this.historyIndex}].coordinates.coordinates`)) {
          return this.geoHistory[this.historyIndex].coordinates.coordinates;
        }
      }
      return this.allowMultiCoordinates ? this.geoCoordinates[0].coordinates : this.geoCoordinates;
    }
  },
  watch: {
    geoHistory() {
      this.drawGeoHistory();
    },
    geoPolygon() {
      this.drawPolygon();
    },
    enableDrawControls(val) {
      if (this.map && this.drawControls) {
        this.drawControls.displayControlsDefault = val;
      }

      // TODO: mapbox does not yet support disabling controls after they have been drawn
      // https://github.com/mapbox/mapbox-gl-draw/issues/824
      // if (val) {
      //   this.map.addControl(this.drawControls);
      // } else {
      //   this.map.removeControl(this.drawControls);
      // }
    }
  },
  created() {
    this.map = null;
  },
  methods: {
    onMapLoaded(event) {
      this.map = event.map;
      this.drawOverlay();
      const zoomToUse = this.calculateZoomToUse();
      if (!this.allowScrollZoom) {
        this.map.scrollZoom.disable();
      } else {
        this.map.scrollZoom.enable();
      }
      this.addToAnimationQueue({
        center: this.markerCoordinates,
        pitch: 0,
        bearing: 0,
        zoom: zoomToUse,
        duration: this.animateFlyIn ? 2000 : 0,
        animate: this.animateFlyIn
      });
      if (this.isNotIE11 && typeof MapboxDraw === 'function' && !this.disablePolygonDrawing) {
        // MapboxDraw is imported via the runtime-script (see in created for `mapbox-gl-draw.js`)
        // but since it is not available on compile-time, ignore the "undefined" warning here
        // eslint-disable-next-line no-undef
        this.drawControls = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            polygon: this.showPolygonControls,
            trash: this.showPolygonControls
          }
        });
        this.map.addControl(this.drawControls);

        // NOTE: if any event-data is persisted (or the features of polygons),
        // extract only the plain JSON-data of features, rather than persisting the whole event
        // there would be some map-dependent data in the objects linking to the map-instance,
        // which leads to a "black" map if this core-maps instance is destroyed and created again
        // so get rid of any wired data by with JSON.parse(JSON.stringify(_)) "workaround"
        const context = this;
        this.map.on('draw.selectionchange', function () {
          // if the marker is not draggable, also do not allow changing the polygon
          // so if the polygon is selected, change the mode right away
          if (!context.markerDraggable) {
            context.drawControls.changeMode('simple_select');
          }
          if (context.polygonEdit) {
            context.emitPolygon(context.polygonEdit);
            context.polygonEdit = null;
          }
        });
        this.map.on('draw.modechange', function (e) {
          if (!context.markerDraggable) {
            context.drawControls.changeMode('simple_select');
            return;
          }
          if (e.mode === 'simple_select') {
            // do not publish in redraw-mode, as this is done in `draw.create` already
            if (!context.polygonRedrawMode && context.polygonEdit) {
              const publish = cloneDeep(context.polygonEdit);
              context.polygonEdit = null;
              context.emitPolygon(publish);
            }
            context.polygonRedrawMode = false;
          }
          // a polygon cannot be deleted atm, as it is just redrawn directly,
          // BUT if the user wants to create a polygon from scratch, delete the current one "internally" here,
          // and start a new drawing-mode
          if (e.mode === 'draw_polygon') {
            context.polygonRedrawMode = true;
            context.drawControls.deleteAll().changeMode('draw_polygon');
          }
        });
        this.map.on('draw.create', function (e) {
          if (!context.markerDraggable) {
            return;
          }
          // { "type": "draw.create",
          //   "features": [ {
          //     "id": "c267756cdcace2d952f164c269bb5b42", "type": "Feature", "properties": {},
          //     "geometry": { "type": "Polygon", "coordinates": [
          //         [6.991878709565981, 51.427654175788234],
          //         [6.993094191170741, 51.42727132077292],
          //       ],
          //     },
          //   } ]
          // }
          context.hasValidPolygon = true;
          context.emitPolygon({
            features: JSON.parse(JSON.stringify(e.features))
          });
        });
        this.map.on('draw.update', function (e) {
          context.polygonRedrawMode = false;
          if (!context.markerDraggable) {
            context.polygonEdit = null;
            return;
          }
          context.polygonEdit = {
            features: JSON.parse(JSON.stringify(e.features))
          };
        });
        this.map.on('draw.delete', function () {
          if (!context.markerDraggable || context.polygonRedrawMode) {
            return;
          }
          context.polygonEdit = null;
          context.hasValidPolygon = false;
          context.$emit('polygonUpdated', null);
        });
      }
      this.drawInitialData();
    },
    drawInitialData() {
      this.drawPolygon();
      this.drawGeoHistory();
    },
    emitPolygon(polygon) {
      const coordinates = get(polygon, 'features[0].geometry.coordinates[0]') || null;
      this.$emit('polygonUpdated', coordinates);
    },
    drawPolygon() {
      if (!this.drawControls || this.disablePolygonDrawing) {
        return;
      }
      this.drawControls.deleteAll();
      if (!this.geoPolygon || this.geoPolygon.length === 0) {
        return;
      }
      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: this.geoPolygon
        },
        properties: {}
      };
      this.drawControls.add(feature);
      this.drawControls.changeMode('simple_select');
      this.hasValidPolygon = true;
    },
    drawOverlay() {
      if (!this.map) {
        return;
      }
      if (!this.map.getSource('empty')) {
        // since there is no direct z-index property, create dummy layers under which stuff is positioned
        // drawOverlay should be called before any draw-controls for polygons are added,
        // as to make sure this is really at the bottom
        this.map.addSource('empty', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });
        this.map.addLayer({
          id: 'groundLayer',
          type: 'symbol',
          source: 'empty'
        });
      }
      const overlayId = 'overlay';
      if (this.map.getLayer(overlayId)) {
        this.map.removeLayer(overlayId);
      }
      if (this.map.getSource(overlayId)) {
        this.map.removeSource(overlayId);
      }
      if (!this.overlayInfo) {
        return;
      }
      if (!this.map.getSource(overlayId)) {
        this.map.addSource(overlayId, {
          type: 'image',
          url: this.overlayInfo.image,
          coordinates: this.overlayInfo.coordinates
        });
      }
      this.map.addLayer({
        id: overlayId,
        source: overlayId,
        type: 'raster',
        paint: {
          'raster-opacity': 1.0
        }
      }, 'groundLayer');
    },
    onMarkerDragged(e) {
      const latLng = e.mapboxEvent.target['_lngLat'];
      this.$emit('markerUpdated', {
        lat: latLng.lat,
        lon: latLng.lng
      });
    },
    historyIndexStepBack() {
      this.historyIndex = this.historyIndex === 0 ? this.geoHistory.length - 1 : this.historyIndex - 1;
    },
    historyIndexStepForward() {
      this.historyIndex = this.historyIndex === this.geoHistory.length - 1 ? 0 : this.historyIndex + 1;
    },
    drawGeoHistory() {
      if (!this.map) {
        return;
      }
      if (this.map.getLayer(kGeoGeometryLayerId)) {
        this.map.removeLayer(kGeoGeometryLayerId);
      }
      if (this.map.getSource(kGeoGeometryLayerId)) {
        this.map.removeSource(kGeoGeometryLayerId);
      }
      if (this.geoHistory) {
        this.historyIndex = (this.geoHistory || []).length - 1;
        this.map.addSource(kGeoGeometryLayerId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: this.geoHistory.map(h => get(h, 'coordinates.coordinates'))
            }
          }
        });
        this.map.addLayer({
          id: kGeoGeometryLayerId,
          type: 'line',
          source: kGeoGeometryLayerId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': this.markerColor,
            'line-opacity': 0.5,
            'line-width': 8
          }
        });
      }
    },
    localizeDate(a) {
      if (a) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(a).format('L LTS');
      }
      return '-';
    }
  }
};